import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CORAInput, MODE_SELECTORS, FULFILLMENT_PROGRAMS } from 'src/features/types/coraTypes';
import CORAForm from 'src/features/components/cora/CORAForm';
import CORAResults from 'src/features/components/cora/CORAResults';
import { useCORAData } from 'src/features/hooks/useCORAData';

const CORAPage = () => {
    const { nodeId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const initialInput: CORAInput = {
        nodeId: nodeId || searchParams.get('nodeId') || '',
        nodeIds: nodeId ? [nodeId] : (searchParams.get('nodeId') ? [searchParams.get('nodeId')!] : []),
        durationOfRiskWindowInDays: Number(searchParams.get('durationOfRiskWindowInDays')) || 7,
        modeSelector: searchParams.get('modeSelector') || MODE_SELECTORS.EXSD,
        fulfillmentProgram: searchParams.get('fulfillmentProgram') || FULFILLMENT_PROGRAMS.AMAZON_FULFILLMENT,
    };

    const [currentInput, setCurrentInput] = useState<CORAInput>(initialInput);
    const [submittedInput, setSubmittedInput] = useState<CORAInput | undefined>(
        initialInput.nodeIds && initialInput.nodeIds.length > 0 ? initialInput : undefined
    );

    const { isLoading } = useCORAData(submittedInput);

    const handleFormSubmit = (input: CORAInput) => {
        setSubmittedInput(input);
        setCurrentInput(input);

        const newSearchParams = new URLSearchParams();
        if (input.durationOfRiskWindowInDays !== 7) {
            newSearchParams.set('durationOfRiskWindowInDays', input.durationOfRiskWindowInDays.toString());
        }
        if (input.modeSelector !== MODE_SELECTORS.EXSD) {
            newSearchParams.set('modeSelector', input.modeSelector);
        }
        if (input.fulfillmentProgram !== FULFILLMENT_PROGRAMS.AMAZON_FULFILLMENT) {
            newSearchParams.set('fulfillmentProgram', input.fulfillmentProgram);
        }
        if (input.nodeIds && input.nodeIds.length === 1) {
            newSearchParams.set('nodeId', input.nodeIds[0]);
        }

        const newUrl = newSearchParams.toString();
        if (newUrl) {
            setSearchParams(newSearchParams);
        } else {
            navigate('/cora', { replace: true });
        }
    };

    useEffect(() => {
        const urlInput: CORAInput = {
            nodeId: nodeId || searchParams.get('nodeId') || '',
            nodeIds: nodeId ? [nodeId] : (searchParams.get('nodeId') ? [searchParams.get('nodeId')!] : []),
            durationOfRiskWindowInDays: Number(searchParams.get('durationOfRiskWindowInDays')) || 7,
            modeSelector: searchParams.get('modeSelector') || MODE_SELECTORS.EXSD,
            fulfillmentProgram: searchParams.get('fulfillmentProgram') || FULFILLMENT_PROGRAMS.AMAZON_FULFILLMENT,
        };

        setCurrentInput(urlInput);

        if (urlInput.nodeIds && urlInput.nodeIds.length > 0) {
            setSubmittedInput(urlInput);
        }
    }, [nodeId, searchParams]);

    return (
        <div className="cora-page">
            {/* Page Title */}
            <h2 className="cora-page-title">CPT Risk Management Tool</h2>

            {/* Form Section */}
            <div className="cora-form-section">
                <CORAForm
                    initialInput={currentInput}
                    onSubmit={handleFormSubmit}
                    isLoading={isLoading}
                />
            </div>

            {/* Results Section */}
            <div className="cora-results-section">
                {/* Section Divider - matches table width */}
                {submittedInput && (
                    <div
                        className="cora-section-divider"
                        style={{
                            borderBottom: '3px solid #333',
                            margin: '10px 0',
                            width: '100%'
                        }}
                    />
                )}
                <CORAResults input={submittedInput} />
            </div>
        </div>
    );
};

export default CORAPage;
